import React from 'react';

/**
 * PublicationDesignRenderer
 * Faithfully renders an imported publication using the "Publication House"
 * design system (teal + gold, Georgia) defined in the design handoff.
 *
 * Components: A Section Header, B H2, C H3, D Body, E Callout, F Table,
 * G Title Block, H Keywords, J Closing Mark.
 */

const T = {
  OBSIDIAN: '#0D1117',
  DEEP_TEAL: '#1B4B5A',
  GOLD: '#B5813B',
  GOLD_LIGHT: '#F5E9D3',
  GOLD_BG: '#FDF6EC',
  SLATE: '#4A5568',
  MIST: '#8A9BAD',
  RULE_LINE: '#D4C5A9',
  OFF_WHITE: '#FAFAF8',
  CALLOUT_TEXT: '#3D2B0F',
  WHITE: '#FFFFFF',
};

const SERIF = "Georgia, 'Times New Roman', serif";

export { T as TOKENS, SERIF as SERIF_FONT };

// ---------- Block renderers ----------

const Body = ({ text, isFirst }) => (
  <p
    data-testid="pub-block-body"
    style={{
      color: T.OBSIDIAN,
      fontFamily: SERIF,
      fontSize: '1.0625rem',
      lineHeight: '1.85',
      textAlign: 'justify',
      margin: '0 0 1.1rem 0',
    }}
  >
    {text}
  </p>
);

const H2 = ({ text }) => (
  <h3
    data-testid="pub-block-h2"
    style={{
      color: T.DEEP_TEAL,
      fontFamily: SERIF,
      fontSize: '1.45rem',
      fontWeight: 700,
      borderBottom: `2px solid ${T.GOLD}`,
      paddingBottom: '0.4rem',
      margin: '2rem 0 1rem 0',
    }}
  >
    {text}
  </h3>
);

const H3 = ({ text }) => (
  <h4
    data-testid="pub-block-h3"
    style={{
      color: T.GOLD,
      fontFamily: SERIF,
      fontSize: '1.0625rem',
      fontWeight: 700,
      fontVariant: 'small-caps',
      letterSpacing: '0.04em',
      margin: '1.4rem 0 0.5rem 0',
    }}
  >
    {text}
  </h4>
);

const Callout = ({ text }) => (
  <div
    data-testid="pub-block-callout"
    style={{
      backgroundColor: T.GOLD_BG,
      borderLeft: `6px solid ${T.GOLD}`,
      padding: '1.1rem 1.4rem',
      margin: '1.5rem 0',
      borderRadius: '0 4px 4px 0',
    }}
  >
    <p
      style={{
        color: T.CALLOUT_TEXT,
        fontFamily: SERIF,
        fontStyle: 'italic',
        fontSize: '1.0625rem',
        lineHeight: '1.7',
        margin: 0,
      }}
    >
      {text}
    </p>
  </div>
);

const DataTable = ({ rows, hasHeader }) => {
  const cell = {
    border: `1px solid ${T.RULE_LINE}`,
    padding: '0.6rem 0.85rem',
    fontFamily: SERIF,
    fontSize: '0.95rem',
    lineHeight: '1.55',
    verticalAlign: 'top',
    color: T.OBSIDIAN,
    whiteSpace: 'pre-line',
  };

  return (
    <div style={{ overflowX: 'auto', margin: '1.5rem 0' }}>
      <table
        data-testid="pub-block-table"
        style={{ width: '100%', borderCollapse: 'collapse', border: `1px solid ${T.RULE_LINE}` }}
      >
        <tbody>
          {rows.map((row, ri) => {
            const isHeaderRow = hasHeader && ri === 0;
            const dataIdx = hasHeader ? ri - 1 : ri;
            const altWarm = dataIdx % 2 === 1;
            return (
              <tr key={ri}>
                {row.map((c, ci) => {
                  if (isHeaderRow) {
                    return (
                      <th
                        key={ci}
                        style={{
                          ...cell,
                          backgroundColor: T.DEEP_TEAL,
                          color: T.WHITE,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.03em',
                          textAlign: 'left',
                          fontSize: '0.85rem',
                        }}
                      >
                        {c}
                      </th>
                    );
                  }
                  // Label column for header-less (two-column) tables
                  const isLabelCol = !hasHeader && ci === 0;
                  return (
                    <td
                      key={ci}
                      style={{
                        ...cell,
                        backgroundColor: isLabelCol
                          ? (altWarm ? T.GOLD_LIGHT : T.GOLD_BG)
                          : (altWarm ? T.OFF_WHITE : T.WHITE),
                        color: isLabelCol ? T.DEEP_TEAL : T.OBSIDIAN,
                        fontWeight: isLabelCol ? 700 : 400,
                      }}
                    >
                      {c}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const SectionHeader = ({ number, title }) => (
  <div
    data-testid="pub-section-header"
    style={{ display: 'flex', alignItems: 'stretch', margin: '2.5rem 0 1.5rem 0', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}
  >
    <div
      style={{
        backgroundColor: T.GOLD,
        color: T.WHITE,
        fontFamily: SERIF,
        fontSize: '1.6rem',
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0.6rem 1rem',
        minWidth: '3.5rem',
      }}
    >
      {number}
    </div>
    <div
      style={{
        backgroundColor: T.DEEP_TEAL,
        color: T.WHITE,
        fontFamily: SERIF,
        fontSize: '1.15rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.12em',
        display: 'flex',
        alignItems: 'center',
        padding: '0.6rem 1.4rem',
        flex: 1,
      }}
    >
      {title}
    </div>
  </div>
);

const ReferenceItem = ({ text }) => (
  <p
    data-testid="pub-block-reference"
    style={{
      color: T.OBSIDIAN,
      fontFamily: SERIF,
      fontSize: '0.95rem',
      lineHeight: '1.6',
      paddingLeft: '2rem',
      textIndent: '-2rem',
      margin: '0 0 0.85rem 0',
    }}
  >
    {text}
  </p>
);

// ---------- Section ----------

const Section = ({ section }) => {
  const isReferences = (section.title || '').toUpperCase() === 'REFERENCES';
  let bodyCount = 0;
  return (
    <section>
      <SectionHeader number={section.number} title={section.title} />
      {section.blocks.map((block, i) => {
        switch (block.type) {
          case 'h2':
            return <H2 key={i} text={block.text} />;
          case 'h3':
            return <H3 key={i} text={block.text} />;
          case 'callout':
            return <Callout key={i} text={block.text} />;
          case 'table':
            return <DataTable key={i} rows={block.rows} hasHeader={block.has_header} />;
          case 'body':
          default:
            if (isReferences) {
              return <ReferenceItem key={i} text={block.text} />;
            }
            return <Body key={i} text={block.text} isFirst={bodyCount++ === 0} />;
        }
      })}
    </section>
  );
};

// ---------- Title block (Component G) + Keywords (H) ----------

const TitleBlock = ({ publication }) => {
  const line1 = publication.title_line1 || publication.title;
  const line2 = publication.title_line2;
  const subtitle = publication.subtitle;
  const author = publication.author;
  const pubDate = publication.pub_date;

  return (
    <header data-testid="pub-title-block" style={{ marginBottom: '1rem' }}>
      {/* Double bar */}
      <div style={{ height: '6px', backgroundColor: T.GOLD }} />
      <div style={{ height: '2px', backgroundColor: T.DEEP_TEAL, marginBottom: '1.75rem' }} />

      <h1
        style={{
          color: T.DEEP_TEAL,
          fontFamily: SERIF,
          fontSize: '2.6rem',
          fontWeight: 700,
          lineHeight: 1.1,
          margin: 0,
        }}
      >
        {line1}
      </h1>
      {line2 && (
        <div
          style={{
            color: T.GOLD,
            fontFamily: SERIF,
            fontSize: '1.9rem',
            fontWeight: 400,
            lineHeight: 1.15,
            marginTop: '0.25rem',
          }}
        >
          {line2}
        </div>
      )}
      {subtitle && (
        <p
          style={{
            color: T.SLATE,
            fontFamily: SERIF,
            fontStyle: 'italic',
            fontSize: '1.05rem',
            borderBottom: `1px solid ${T.RULE_LINE}`,
            paddingBottom: '1rem',
            margin: '1.25rem 0 1rem 0',
          }}
        >
          {subtitle}
        </p>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2.5rem', marginTop: '0.75rem' }}>
        {author && (
          <div>
            <div style={{ color: T.MIST, fontFamily: SERIF, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.15em' }}>Authors</div>
            <div style={{ color: T.SLATE, fontFamily: SERIF, fontSize: '0.95rem', marginTop: '0.15rem' }}>{author}</div>
          </div>
        )}
        {pubDate && (
          <div>
            <div style={{ color: T.MIST, fontFamily: SERIF, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.15em' }}>Date</div>
            <div style={{ color: T.SLATE, fontFamily: SERIF, fontSize: '0.95rem', marginTop: '0.15rem' }}>{pubDate}</div>
          </div>
        )}
      </div>
    </header>
  );
};

const KeywordsLine = ({ keywords }) => {
  if (!keywords || keywords.length === 0) return null;
  return (
    <div
      data-testid="pub-keywords"
      style={{
        borderTop: `1px solid ${T.RULE_LINE}`,
        borderBottom: `1px solid ${T.RULE_LINE}`,
        padding: '0.65rem 0',
        margin: '1.5rem 0',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '0.5rem',
      }}
    >
      <span style={{ color: T.MIST, fontFamily: SERIF, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.15em', marginRight: '0.5rem' }}>
        Keywords
      </span>
      <span style={{ color: T.SLATE, fontFamily: SERIF, fontStyle: 'italic', fontSize: '0.95rem' }}>
        {keywords.join('  ·  ')}
      </span>
    </div>
  );
};

const ClosingMark = () => (
  <div data-testid="pub-closing-mark" style={{ display: 'flex', height: '8px', marginTop: '3rem' }}>
    <div style={{ flex: '0 0 22%', backgroundColor: T.GOLD_LIGHT }} />
    <div style={{ flex: 1, backgroundColor: T.GOLD }} />
    <div style={{ flex: '0 0 22%', backgroundColor: T.DEEP_TEAL }} />
  </div>
);

const CopyrightNotice = () => (
  <div
    data-testid="pub-copyright"
    style={{ marginTop: '2.5rem', background: '#fef2f2', border: '4px solid #ef4444', padding: '1.5rem 1.75rem', borderRadius: '8px' }}
  >
    <p style={{ textAlign: 'center', color: '#1f2937', fontWeight: 600, fontFamily: SERIF, margin: 0, lineHeight: 1.6 }}>
      © {new Date().getFullYear()} AcuErnie. All rights reserved. This publication is for view only.
      Unauthorized reproduction, distribution, or copying is strictly prohibited.
    </p>
  </div>
);

// ---------- Root ----------

export const PublicationDesignRenderer = ({ publication }) => {
  const sections = publication.sections || [];
  return (
    <div
      data-testid="publication-design-render"
      style={{ backgroundColor: T.WHITE, padding: '2.5rem clamp(1.25rem, 5vw, 4rem)', borderRadius: '6px', boxShadow: '0 10px 40px rgba(0,0,0,0.08)' }}
    >
      <TitleBlock publication={publication} />
      <KeywordsLine keywords={publication.keywords} />
      {sections.map((section, i) => (
        <Section key={i} section={section} />
      ))}
      <ClosingMark />
      <CopyrightNotice />
    </div>
  );
};

export default PublicationDesignRenderer;
